/**
 * Why: Rule 4 of the PII sub-spec is the net that catches PII we didn't
 * anticipate via field-name matching — emails and phone numbers can appear
 * inside free-form message text, log lines, exception messages, or
 * breadcrumb data. Regex scrubbing is a different kind of operation than
 * key-based redaction; isolating it makes each rule testable.
 * Scope: Adding a new pattern goes in `REGEX_PATTERNS` here only. This
 * module becomes the sole source of truth for "what shapes of data should
 * never hit Sentry from the browser".
 * SRP: Apply a fixed set of regex redactions to strings and string-leaves
 * of nested objects/arrays.
 *
 * Mirrors `app/Sentry/StringPatternScrubber.php` in the API repo.
 */

const REGEX_PATTERNS = [
  [/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, '[EMAIL]'],
  [/\+\d{10,15}/g, '[PHONE]'],
  [/\b\d{3}-?\d{2}-?\d{4}\b/g, '[SSN]'],
  [/AC[a-f0-9]{32}/g, '[TWILIO_SID]'],
  [/Bearer\s+[A-Za-z0-9\-._~+/]+=*/g, 'Bearer [REDACTED]'],
];

/**
 * Why: The single trusted entry point for regex scrubbing. Callers pass
 * in any string they'd otherwise transmit to Sentry; we apply every
 * pattern sequentially and return a safe version.
 * Scope: Adding a pre-processing step (e.g. unicode normalization) goes
 * here; callers don't change.
 * SRP: Return a redacted copy of a single string.
 *
 * @param {string} value Raw string that may contain PII
 * @returns {string}     Same string with every known pattern replaced
 */
export function scrub(value) {
  if (typeof value !== 'string') {
    return value;
  }
  let result = value;
  REGEX_PATTERNS.forEach(([pattern, replacement]) => {
    result = result.replace(pattern, replacement);
  });
  return result;
}

/**
 * Why: Breadcrumbs and contexts carry nested structures whose leaves are
 * user-generated strings. A single entry could bury an email three levels
 * deep. Walking the structure ensures no leaf escapes the pattern scrub.
 * Scope: Non-string leaves (number, boolean, null) pass through untouched.
 * SRP: Apply `scrub()` to every string leaf in a nested object/array.
 *
 * @param {*} data Arbitrary nested structure
 * @returns {*}    Same shape with string leaves scrubbed
 */
export function scrubStringsIn(data) {
  if (Array.isArray(data)) {
    return data.map((item) => scrubStringsIn(item));
  }
  if (data === null || typeof data !== 'object') {
    return typeof data === 'string' ? scrub(data) : data;
  }
  const result = {};
  Object.keys(data).forEach((key) => {
    result[key] = scrubStringsIn(data[key]);
  });
  return result;
}
